import isFunction from 'lodash-es/isFunction';

interface BlockAddon {
    block: string;
    max?: number | null;
    interval?: number | null;
    minHeight?: number | null;
    notAfter?: string[] | null;
    notBefore?: string[] | null;
    notAround?: string[] | null;
    minBlocks?: number | null;
    [key: string]: unknown;
}

interface GetBlocksWithAddonsOptions {
    addons?: BlockAddon[];
    minSpaceBetween?: number | null;
    addonsProps?: Record<
        string,
        unknown | ((args: { count: number; block: Block; index: number }) => unknown)
    >;
}

interface BlocksAddonsState {
    height?: number;
    heightSinceLastAddons?: number;
    [key: string]:
        | {
              count: number;
              height: number;
              blocksCount: number;
          }
        | number;
}

export function getBlockHeight({
    type = null,
    body = null,
}: {
    type?: string | null;
    body?: string | null;
}) {
    if (body !== null && type === 'quote') {
        return body.length / 100;
    }
    if (body !== null && type === 'heading') {
        return body.length / 100;
    }
    if (body !== null) {
        return body.length / 250;
    }
    if (type === 'embed' || type === 'image' || type === 'gallery' || type === 'images') {
        return 2;
    }
    return 1;
}

export const adAddon = {
    block: 'ad',
    max: 30,
    interval: 4,
    minHeight: 2,
    minBlocks: 2,
    notAfter: ['heading'],
    notBefore: ['quote', 'image'],
    // notAround: ['quote', 'image'],
};

export const subscriptionAddon = {
    block: 'subscription',
    max: 1,
    interval: 5,
    minHeight: 0,
    notAfter: ['heading'],
    notAround: ['quote', 'image', 'embed'],
};

// Rendered only when a campaign targets the document (see FormCampaignBlock)
export const formCampaignAddon = {
    block: 'form-campaign',
    max: 1,
    interval: 8,
    minHeight: 0,
    notAfter: ['heading'],
    notAround: ['quote', 'image', 'embed', 'video'],
};

const defaultAddons = [adAddon, subscriptionAddon, formCampaignAddon];

function getBlocksWithAddons(
    blocks: Block[],
    {
        addons = defaultAddons,
        minSpaceBetween = 2,
        addonsProps = null,
    }: GetBlocksWithAddonsOptions = {},
): Block[] {
    const { blocks: newBlocks = [] } =
        blocks.length > 0
            ? blocks.reduce<{
                  blocks: Block[];
                  state: BlocksAddonsState;
              }>(
                  ({ blocks: currentBlocks = [], state: curentState = {} }, block, index) => {
                      const nextBlock = index < blocks.length - 1 ? blocks[index + 1] : null;
                      const blockHeight = getBlockHeight(block);
                      const { type: prevType } = block;
                      const { type: nextType, body: nextBody = null } = nextBlock || {};
                      const blockIsOkay = nextType !== 'text' || nextBody.length > 75;

                      const { height: totalHeight = 0, heightSinceLastAddons = 0 } = curentState;
                      const newTotalHeight = totalHeight + blockHeight;
                      const newHeightSinceLastAddons = heightSinceLastAddons + blockHeight;

                      return addons.reduce(
                          (
                              { blocks: currentAddonsBlocks, state: currentAddonsState = {} },
                              addon,
                          ) => {
                              const {
                                  block: addonBlock,
                                  max = 1,
                                  interval = null,
                                  minHeight = null,
                                  notAfter = null,
                                  notBefore = null,
                                  notAround = null,
                                  minBlocks = null,
                                  ...customAddonProps
                              } = addon;
                              const { heightSinceLastAddons: currentHeightSinceLastAddons } =
                                  currentAddonsState;
                              const addonProps = addonsProps?.[addonBlock] || null;
                              const {
                                  count = 0,
                                  height = 0,
                                  blocksCount = 0,
                              } = currentAddonsState[addonBlock] || {};
                              const newHeight = height + blockHeight;
                              const newBlocksCount = blocksCount + 1;
                              const needAddons =
                                  blockIsOkay &&
                                  (notAfter === null || notAfter.indexOf(prevType) === -1) &&
                                  (notBefore === null || notBefore.indexOf(nextType) === -1) &&
                                  (notAround === null ||
                                      (notAround.indexOf(prevType) === -1 &&
                                          notAround.indexOf(nextType) === -1)) &&
                                  (interval === null || newHeight >= interval) &&
                                  (max === null || count < max) &&
                                  (minHeight === null || newTotalHeight >= minHeight) &&
                                  (minSpaceBetween === null ||
                                      currentHeightSinceLastAddons >= minSpaceBetween) &&
                                  (minBlocks === null || newBlocksCount >= minBlocks);
                              return {
                                  blocks: needAddons
                                      ? [
                                            ...currentAddonsBlocks,
                                            {
                                                id: `${addonBlock}-${block.id || index}`,
                                                type: addonBlock,
                                                ...(isFunction(addonProps)
                                                    ? addonProps({
                                                          count,
                                                          block,
                                                          index,
                                                      })
                                                    : addonProps),
                                                ...customAddonProps,
                                            },
                                        ]
                                      : currentAddonsBlocks,
                                  state: {
                                      ...currentAddonsState,
                                      heightSinceLastAddons: needAddons
                                          ? 0
                                          : currentHeightSinceLastAddons,
                                      [addonBlock]: {
                                          count: count + (needAddons ? 1 : 0),
                                          height: needAddons ? 0 : newHeight,
                                          blocksCount: needAddons ? 0 : newBlocksCount,
                                      },
                                  },
                              };
                          },
                          {
                              blocks: [...currentBlocks, block],
                              state: {
                                  ...curentState,
                                  heightSinceLastAddons: newHeightSinceLastAddons,
                                  height: newTotalHeight,
                              },
                          },
                      );
                  },
                  {
                      blocks: [],
                      state: {
                          heightSinceLastAddons: 0,
                          height: 0,
                      },
                  },
              )
            : {};
    return newBlocks;
}

export default getBlocksWithAddons;
